import {
  IDLE_INTENT_STATE,
  toWorld,
  type Intent,
  type InputContext,
  type InputSource,
  type IntentState,
} from './intent';
import { dragStick, pressStick, type Thumbstick } from './thumbstick';

const ABILITY_CAST_FRACTION = 0.3;

const PAN_AXIS_THRESHOLD = 0.3827;

export interface TouchControlsView {
  readonly enabled: boolean;
  readonly stick: Thumbstick | null;
  readonly firing: boolean;
}

export class TouchInputSource implements InputSource {
  private touchMode = false;
  private stickPointerId: number | null = null;
  private stick: Thumbstick | null = null;
  private firing = false;
  private heldAngle = 0;
  private cssViewportWidth = 0;
  private readonly pending: Intent[] = [];
  private lastState: IntentState = IDLE_INTENT_STATE;
  private readonly listeners = new Set<() => void>();

  constructor() {
    window.addEventListener('pointerdown', this.onWindowPointer, true);
    window.addEventListener('pointermove', this.onWindowPointer, true);
    window.addEventListener('blur', this.onBlur);
  }

  get driving(): boolean {
    return this.touchMode;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  snapshot(): TouchControlsView {
    return { enabled: this.touchMode, stick: this.stick, firing: this.firing };
  }

  attachStickZone(element: HTMLElement): () => void {
    const down = (event: PointerEvent): void => {
      if (event.pointerType !== 'touch') return;
      if (!this.beginStick(event.pointerId, event.clientX, event.clientY)) return;
      element.setPointerCapture(event.pointerId);
      event.preventDefault();
    };
    const move = (event: PointerEvent): void => {
      if (this.moveStick(event.pointerId, event.clientX, event.clientY)) event.preventDefault();
    };
    const up = (event: PointerEvent): void => {
      this.endStick(event.pointerId);
    };

    element.addEventListener('pointerdown', down);
    element.addEventListener('pointermove', move);
    element.addEventListener('pointerup', up);
    element.addEventListener('pointercancel', up);

    return () => {
      element.removeEventListener('pointerdown', down);
      element.removeEventListener('pointermove', move);
      element.removeEventListener('pointerup', up);
      element.removeEventListener('pointercancel', up);
    };
  }

  beginStick(pointerId: number, cssX: number, cssY: number): boolean {
    if (this.stickPointerId !== null) return false;
    if (this.cssViewportWidth > 0 && cssX > this.cssViewportWidth / 2) return false;

    this.setTouchMode(true);
    this.stickPointerId = pointerId;
    this.stick = pressStick(cssX, cssY);
    this.notify();
    return true;
  }

  moveStick(pointerId: number, cssX: number, cssY: number): boolean {
    const stick = this.stick;
    if (stick === null || pointerId !== this.stickPointerId) return false;
    this.stick = dragStick(stick.baseX, stick.baseY, cssX, cssY);
    this.notify();
    return true;
  }

  endStick(pointerId: number): void {
    if (pointerId !== this.stickPointerId) return;
    this.stickPointerId = null;
    this.stick = null;
    this.notify();
  }

  pressFire(): void {
    this.firing = true;
    this.pending.push({ kind: 'fireOnce' });
    this.notify();
  }

  releaseFire(): void {
    if (!this.firing) return;
    this.firing = false;
    this.notify();
  }

  pressAbility(): void {
    this.pending.push({
      kind: 'useAbilityAt',
      worldX: this.lastState.pointerWorldX,
      worldY: this.lastState.pointerWorldY,
    });
  }

  sample(context: InputContext): IntentState {
    this.cssViewportWidth = context.viewportWidth / context.pixelRatio;

    const stick = this.stick;
    const magnitude = stick?.magnitude ?? 0;
    /** Held on release rather than reset: 0 is not "no heading", it is due east. */
    if (stick !== null && magnitude > 0) this.heldAngle = stick.angle;

    const castPx = ABILITY_CAST_FRACTION * Math.min(context.viewportWidth, context.viewportHeight);
    const cast = toWorld(
      context.viewportWidth / 2 + Math.cos(this.heldAngle) * castPx,
      context.viewportHeight / 2 + Math.sin(this.heldAngle) * castPx,
      context,
    );

    this.lastState = {
      aimAngle: this.heldAngle,
      moving: magnitude > 0,
      firing: this.firing,
      braking: false,
      pointerWorldX: cast.x,
      pointerWorldY: cast.y,
      panAxes: panAxesFor(stick),
    };
    return this.lastState;
  }

  releaseAll(): void {
    if (this.stick === null && this.stickPointerId === null && !this.firing) return;
    this.stickPointerId = null;
    this.stick = null;
    this.firing = false;
    this.notify();
  }

  contributes(): boolean {
    return this.driving;
  }

  drain(): Intent[] {
    if (this.pending.length === 0) return [];
    return this.pending.splice(0, this.pending.length);
  }

  dispose(): void {
    window.removeEventListener('pointerdown', this.onWindowPointer, true);
    window.removeEventListener('pointermove', this.onWindowPointer, true);
    window.removeEventListener('blur', this.onBlur);
    this.listeners.clear();
  }

  private readonly onWindowPointer = (event: PointerEvent): void => {
    if (event.pointerType === 'touch') this.setTouchMode(true);
    else if (event.pointerType === 'mouse') this.setTouchMode(false);
  };

  private readonly onBlur = (): void => {
    this.releaseAll();
  };

  private setTouchMode(on: boolean): void {
    if (this.touchMode === on) return;
    this.touchMode = on;
    if (!on) this.releaseAll();
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

function panAxesFor(stick: Thumbstick | null): [boolean, boolean, boolean, boolean] {
  if (stick === null || stick.magnitude === 0) return [false, false, false, false];
  const cos = Math.cos(stick.angle);
  const sin = Math.sin(stick.angle);
  return [
    cos < -PAN_AXIS_THRESHOLD,
    sin < -PAN_AXIS_THRESHOLD,
    cos > PAN_AXIS_THRESHOLD,
    sin > PAN_AXIS_THRESHOLD,
  ];
}
